/**
 * Bill of Lading List Page
 *
 * Displays a paginated list of all bills of lading.
 * Features:
 * - Filters (search by B/L number, status, document status, type)
 * - Table view with sortable columns including Carrier, Shipper, Containers, Issue Date
 * - Pagination
 * - Quick actions (view, archive, delete)
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as billOfLadingApi from '../api/billOfLadingApi';
import Button from '../components/common/Button';
import SparklesIcon from '../components/common/SparklesIcon';

/**
 * Record status badge (ACTIVE / ARCHIVED)
 */
function StatusBadge({ status }) {
  const colors = {
    ACTIVE: 'bg-green-100 text-green-800',
    ARCHIVED: 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${colors[status] || colors.ACTIVE}`}>
      {status}
    </span>
  );
}

/**
 * Transport document type badge (BOL / SWB)
 */
function TypeBadge({ typeCode }) {
  const colors = {
    BOL: 'bg-blue-100 text-blue-800',
    SWB: 'bg-gray-100 text-gray-800'
  };

  if (!typeCode) return <span className="text-gray-400 text-xs">—</span>;

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${colors[typeCode] || 'bg-gray-100 text-gray-800'}`}>
      {typeCode}
    </span>
  );
}

/**
 * Document status badge (DRAFT / ISSUED / SURRENDERED / ACCOMPLISHED / VOIDED)
 */
function DocumentStatusBadge({ documentStatus }) {
  const colors = {
    DRAFT: 'bg-gray-100 text-gray-700',
    ISSUED: 'bg-blue-100 text-blue-800',
    SURRENDERED: 'bg-yellow-100 text-yellow-800',
    ACCOMPLISHED: 'bg-green-100 text-green-800',
    VOIDED: 'bg-red-100 text-red-800'
  };

  if (!documentStatus) return <span className="text-gray-400 text-xs">—</span>;

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${colors[documentStatus] || 'bg-gray-100 text-gray-700'}`}>
      {documentStatus}
    </span>
  );
}

/**
 * Actions dropdown component
 */
function ActionsDropdown({ bol, onArchive, onDelete, isProcessing }) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const openMenu = (e) => {
    e.stopPropagation();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 144 }); // 144 = w-36
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={openMenu}
        className="p-1 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
        disabled={isProcessing}
      >
        {isProcessing ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        )}
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
          />
          <div
            className="fixed w-36 bg-white rounded-md shadow-lg border border-gray-200 z-20"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            <div className="py-1">
              {bol.status !== 'ARCHIVED' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                    onArchive(bol._id);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                  Archive
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  onDelete(bol._id);
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Sortable column header component
 */
function SortableHeader({ field, label, sortField, sortDirection, onSort, align = 'left' }) {
  const isActive = sortField === field;

  return (
    <th
      className={`px-4 py-3 text-${align} text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none`}
      onClick={() => onSort(field)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        <span>{label}</span>
        <span className="flex flex-col">
          <svg
            className={`w-3 h-3 -mb-1 ${isActive && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M5 12l5-5 5 5H5z" />
          </svg>
          <svg
            className={`w-3 h-3 ${isActive && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M5 8l5 5 5-5H5z" />
          </svg>
        </span>
      </div>
    </th>
  );
}

/**
 * Get party name by type from the parties array.
 *
 * @param {Array} parties - Array of party objects with { type, name }
 * @param {string} type - Party type to find (e.g. 'CARRIER')
 * @returns {string} Party name or 'N/A'
 */
function getPartyName(parties, type) {
  if (!parties || parties.length === 0) return 'N/A';
  const party = parties.find((p) => p.type === type);
  return party?.name || 'N/A';
}

/**
 * Get total container count from utilizedTransportEquipment or a virtual field.
 *
 * @param {Object} bol - Bill of Lading record
 * @returns {string} Container count or 'N/A'
 */
function getContainerCount(bol) {
  if (bol.totalContainerCount != null) return String(bol.totalContainerCount);
  const equipment = bol.utilizedTransportEquipment;
  if (!equipment || equipment.length === 0) return 'N/A';
  return String(equipment.length);
}

/**
 * BillOfLadingList Component
 */
function BillOfLadingList() {
  const location = useLocation();
  const navigate = useNavigate();

  // State
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);
  const [processingId, setProcessingId] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [documentStatusFilter, setDocumentStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  // Sort state
  const [sortField, setSortField] = useState('issueDate');
  const [sortDirection, setSortDirection] = useState('desc');

  /**
   * Build filter params object for the API call
   */
  const getFilterParams = () => {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (statusFilter) params.status = statusFilter;
    if (documentStatusFilter) params.documentStatus = documentStatusFilter;
    if (typeFilter) params.transportDocumentTypeCode = typeFilter;
    return params;
  };

  /**
   * Fetch bills of lading from the API
   */
  const fetchBills = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await billOfLadingApi.listBillsOfLading({
        page,
        limit: 10,
        ...getFilterParams()
      });

      setBills(response.data || []);
      setTotalPages(response.meta?.totalPages || 1);
      setTotal(response.meta?.total || 0);
    } catch (err) {
      setError(err.message || 'Failed to load bills of lading');
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when page or filters change
  useEffect(() => {
    fetchBills();
  }, [page, searchQuery, statusFilter, documentStatusFilter, typeFilter]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  /**
   * Reset all filters to defaults
   */
  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setDocumentStatusFilter('');
    setTypeFilter('');
    setPage(1);
  };

  /**
   * Archive a bill of lading
   */
  const handleArchive = async (id) => {
    try {
      setProcessingId(id);
      await billOfLadingApi.updateDocumentStatus(id, 'ARCHIVED');
      setSuccessMessage('Bill of lading archived');
      fetchBills();
    } catch (err) {
      setError(err.message || 'Failed to archive bill of lading');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Delete a bill of lading
   */
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bill of lading? This action cannot be undone.')) {
      return;
    }

    try {
      setProcessingId(id);
      await billOfLadingApi.deleteBillOfLading(id);
      setSuccessMessage('Bill of lading deleted');
      fetchBills();
    } catch (err) {
      setError(err.message || 'Failed to delete bill of lading');
    } finally {
      setProcessingId(null);
    }
  };

  /**
   * Format a date string for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const hasActiveFilters = searchQuery || statusFilter || documentStatusFilter || typeFilter;

  /**
   * Handle column header click for sorting
   */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /**
   * Return client-side sorted copy of the bills array.
   * Sorting is done on the current page's data only — server-side
   * sorting would require a separate API parameter.
   */
  const getSortedBills = () => {
    return [...bills].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'blNumber':
          aValue = a.header?.transportDocumentReference || '';
          bValue = b.header?.transportDocumentReference || '';
          break;
        case 'type':
          aValue = a.header?.transportDocumentTypeCode || '';
          bValue = b.header?.transportDocumentTypeCode || '';
          break;
        case 'documentStatus':
          aValue = a.header?.documentStatus || '';
          bValue = b.header?.documentStatus || '';
          break;
        case 'carrier':
          aValue = getPartyName(a.parties, 'CARRIER').toLowerCase();
          bValue = getPartyName(b.parties, 'CARRIER').toLowerCase();
          break;
        case 'shipper':
          aValue = getPartyName(a.parties, 'SHIPPER').toLowerCase();
          bValue = getPartyName(b.parties, 'SHIPPER').toLowerCase();
          break;
        case 'containers':
          // Sort numerically by container count
          aValue = parseInt(getContainerCount(a), 10) || 0;
          bValue = parseInt(getContainerCount(b), 10) || 0;
          break;
        case 'issueDate':
          aValue = a.header?.issueDate ? new Date(a.header.issueDate).getTime() : 0;
          bValue = b.header?.issueDate ? new Date(b.header.issueDate).getTime() : 0;
          break;
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedBills = getSortedBills();

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bills of Lading</h1>
          <p className="text-gray-600">{total} total bills of lading</p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="ml-2 text-sm text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* B/L Number Search */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">B/L Number</label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search B/L number..."
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[200px]"
            />
          </div>

          {/* Status Filter */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>

          {/* Document Status Filter */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">Doc Status</label>
            <select
              value={documentStatusFilter}
              onChange={(e) => {
                setDocumentStatusFilter(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Doc Status</option>
              <option value="DRAFT">Draft</option>
              <option value="ISSUED">Issued</option>
              <option value="SURRENDERED">Surrendered</option>
              <option value="ACCOMPLISHED">Accomplished</option>
              <option value="VOIDED">Voided</option>
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Types</option>
              <option value="BOL">BOL</option>
              <option value="SWB">SWB</option>
            </select>
          </div>

          {/* Reset Filters */}
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : bills.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-4">No bills of lading found</p>
            {hasActiveFilters && (
              <button
                onClick={resetFilters}
                className="mt-2 text-blue-600 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader
                    field="blNumber"
                    label="B/L Number"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="type"
                    label="Type"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="documentStatus"
                    label="Doc Status"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="carrier"
                    label="Carrier"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="shipper"
                    label="Shipper"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="containers"
                    label="Containers"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="issueDate"
                    label="Issue Date"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    field="createdAt"
                    label="Created"
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedBills.map((bol) => (
                  <tr
                    key={bol._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/bills-of-lading/${bol._id}`)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-sm font-medium text-blue-600 hover:text-blue-800">
                        {bol.header?.transportDocumentReference || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <TypeBadge typeCode={bol.header?.transportDocumentTypeCode} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <DocumentStatusBadge documentStatus={bol.header?.documentStatus} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {getPartyName(bol.parties, 'CARRIER')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {getPartyName(bol.parties, 'SHIPPER')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {getContainerCount(bol)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(bol.header?.issueDate)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(bol.createdAt)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <ActionsDropdown
                          bol={bol}
                          onArchive={handleArchive}
                          onDelete={handleDelete}
                          isProcessing={processingId === bol._id}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </p>
            <div className="flex space-x-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BillOfLadingList;
